import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { NgComponentOutlet } from '@angular/common';
import { DraftEntity, EntityType } from '@panaya/ai-chat/shared';
import { applyMapping, entityTitle } from '../../mapping/apply-mapping';
import { CanvasEntityMappingRegistry } from '../../mapping/canvas-entity-mapping.registry';
import { EntityDisplay } from '../../mapping/entity-display.model';
import { reportMissingPaths, reportUnmappedEntityType } from '../../mapping/report-missing-paths';
import { CanvasEntityRendererRegistry } from '../../renderer/canvas-entity-renderer.registry';
import { GenericEntityRendererComponent } from '../../renderer/generic-entity-renderer/generic-entity-renderer.component';

/**
 * The body of one entity: the host's renderer for its type when one is registered, else the generic renderer over the
 * data converted through the type's mapping. A type with neither has no tabs to show.
 */
@Component({
    standalone: true,
    selector: 'p-ai-chat-draft-entity',
    imports: [NgComponentOutlet, GenericEntityRendererComponent],
    templateUrl: './draft-entity.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DraftEntityComponent {
    private readonly mappings = inject(CanvasEntityMappingRegistry);

    private readonly renderers = inject(CanvasEntityRendererRegistry);

    protected readonly hostRenderer = computed(() => this.renderers.resolve(this.entityType()));

    protected readonly hostInputs = computed(() => ({ entity: this.entity() }));

    /**
     * Converted again only when the entity object changes, which is a new draft version, never a checkbox click or a
     * change-detection pass. Read only when no host renderer is registered.
     */
    protected readonly display = computed(() => this.convert(this.entity(), this.entityType()));

    public readonly entity = input.required<DraftEntity>();

    public readonly entityType = input.required<EntityType>();

    private convert(entity: DraftEntity, entityType: EntityType): EntityDisplay {
        const mapping = this.mappings.find(entityType);

        if (!mapping) {
            reportUnmappedEntityType(entityType, entity.id);

            return { title: entityTitle(entity.data, null), tabs: [], missingPaths: [] };
        }

        const display = applyMapping(entity.data, mapping);

        reportMissingPaths(entityType, entity.id, display.missingPaths);

        return display;
    }
}
